import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SignupProvider } from './contexts/SignupContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import AnalyticsTracker from './components/AnalyticsTracker';
import { initGA } from './utils/analytics';
import { Navigate, useParams } from 'react-router-dom';
// Provider type validation is handled within individual components
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import SignupPage from './pages/SignupPage';
import ProfilePage from './pages/ProfilePage';
import FavoritesPage from './pages/FavoritesPage';
import ReviewsPage from './pages/ReviewsPage';
import UserPostsPage from './pages/UserPostsPage';
import PostsPage from './pages/PostsPage';
import PostDetailPage from './pages/PostDetailPage';
import CreatePostPage from './pages/CreatePostPage';
import EditPostPage from './pages/EditPostPage';
import FavoritePostsPage from './pages/FavoritePostsPage';
import EditHairstylistPage from './pages/EditHairstylistPage';
import AdminHairstylistCreatePage from './pages/AdminHairstylistCreatePage';
import AboutPage from './pages/AboutPage';
import HairstylistPage from './pages/HairstylistPage';
import PaymentPage from './pages/PaymentPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import ChatAdminPage from './pages/ChatAdminPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import FAQPage from './pages/FAQPage';
import AdminAnalyticsDashboard from './pages/AdminAnalyticsDashboard';
import AdminVerifyEmail from './pages/AdminVerifyEmail';
import AdminDeleteEvents from './pages/AdminDeleteEvents';
import AdminPendingEmails from './pages/AdminPendingEmails';
import HairstylistDashboard from './pages/HairstylistDashboardPremium';
import ChatWidget from './components/ChatWidget';

// Composant pour rediriger les anciennes routes vers les nouvelles
function RedirectHairstylist() {
  const { id } = useParams();
  return <Navigate to={`/provider/hair/${id}`} replace />;
}

function RedirectEditHairstylist() {
  const { id } = useParams();
  return <Navigate to={`/provider/hair/${id}/edit`} replace />;
}

function RedirectActivate() {
  const { profileId } = useParams();
  return <Navigate to={`/provider/hair/${profileId}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const state = location.state || {};
  const backgroundLocation = state.background || null;

  // Empêcher le scroll de la page derrière quand une modale est ouverte
  useEffect(() => {
    if (backgroundLocation) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [backgroundLocation]);

  return (
    <>
      {/* Routes principales : utilisent soit la location normale, soit la backgroundLocation */}
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<HomePage />} />
        
        {/* Routes génériques pour les prestataires */}
        <Route path="/search/:type" element={<SearchPage />} />
        <Route path="/signup/:type" element={<SignupPage />} />
        <Route path="/provider/:type/:id" element={<HairstylistPage />} />
        <Route path="/provider/:type/:id/edit" element={<EditHairstylistPage />} />
        
        {/* Routes de rétrocompatibilité (redirigent vers les routes génériques avec type='hair') */}
        <Route path="/search" element={<Navigate to="/search/hair" replace />} />
        <Route path="/signup" element={<Navigate to="/signup/hair" replace />} />
        <Route path="/hairstylist/:id" element={<RedirectHairstylist />} />
        <Route path="/hairstylist/edit/:id" element={<RedirectEditHairstylist />} />
        <Route path="/activate/:profileId" element={<RedirectActivate />} />
        
        <Route path="/admin/hairstylists/create" element={<AdminHairstylistCreatePage />} />
        <Route path="/signup/payment" element={<PaymentPage />} />
        <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
        <Route path="/email-verified" element={<EmailVerificationPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/settings" element={<AccountSettingsPage />} />
        <Route path="/profile/favorites" element={<FavoritesPage />} />
        <Route path="/profile/reviews" element={<ReviewsPage />} />
        <Route path="/profile/posts" element={<UserPostsPage />} />
        <Route path="/profile/favorite-posts" element={<FavoritePostsPage />} />
        <Route path="/posts" element={<PostsPage />} />
        <Route path="/posts/:postId" element={<PostDetailPage />} />
        <Route path="/posts/create" element={<CreatePostPage />} />
        <Route path="/posts/edit/:postId" element={<EditPostPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/admin/chat" element={<ChatAdminPage />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsDashboard />} />
        <Route path="/admin/verify-email" element={<AdminVerifyEmail />} />
        <Route path="/admin/delete-events" element={<AdminDeleteEvents />} />
        <Route path="/admin/pending-emails" element={<AdminPendingEmails />} />
        <Route path="/dashboard" element={<HairstylistDashboard />} />
      </Routes>

      {/* Routes modales : s'affichent par-dessus une backgroundLocation */}
      {backgroundLocation && (
        <Routes>
          <Route
            path="/provider/:type/:id"
            element={
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-scroll">
                    <HairstylistPage />
                  </div>
                </div>
              </div>
            }
          />
          {/* Rétrocompatibilité pour les routes modales */}
          <Route
            path="/hairstylist/:id"
            element={
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-scroll">
                    <HairstylistPage />
                  </div>
                </div>
              </div>
            }
          />
        </Routes>
      )}
    </>
  );
}

function App() {
  useEffect(() => {
    // Initialiser Google Analytics une fois que le composant est monté
    initGA();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <SignupProvider>
          <Router>
            <ScrollToTop />
            <AnalyticsTracker />
            <Header />
            <AppRoutes />
            <ChatWidget />
            <Footer />
          </Router>
        </SignupProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
